import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { FileText, Download, Loader2 } from 'lucide-react';
import { api, resolveImage } from '../lib/api';
import { useSiteData } from '../lib/SiteDataContext';

const categoriesMap = {
  policies: 'Policies & Strategies',
  reports: 'Reports & Publications',
  laws: 'Laws & Regulations'
};

const DocumentsPage = () => {
  const { category } = useParams();
  const { settings } = useSiteData();
  const header = settings?.page_headers?.documents || {};
  const key = category && categoriesMap[category] ? category : 'policies';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, [key]);
  useEffect(() => {
    setLoading(true);
    api.get('/documents').then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => items.filter((d) => d.category === key).sort((a, b) => a.order - b.order),
    [items, key]
  );

  return (
    <>
      <SEO
        title={`Documents — ${categoriesMap[key]}`}
        description={`Download official ${categoriesMap[key].toLowerCase()} of the South East State of Somalia State House.`}
        keywords="Somalia government documents, policies, reports, laws, South East State publications"
      />
      <PageHeader
        title={header.title || 'Documents'}
        subtitle={header.subtitle}
        crumbs={[{ label: 'Documents' }]}
      />
      <section className="bg-white py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="mb-8 flex flex-wrap gap-2">
            {Object.entries(categoriesMap).map(([k, label]) => (
              <Link
                key={k}
                to={`/documents/${k}`}
                className={`rounded-full border px-4 py-1.5 text-[13px] font-medium transition ${
                  key === k
                    ? 'border-[#C9A86A] bg-[#C9A86A] text-white'
                    : 'border-[#E6DDC8] bg-white text-[#0B2545] hover:border-[#C9A86A]'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-[#0B2545]/60"><Loader2 size={16} className="animate-spin" /> Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-[#E6DDC8] bg-[#FAF6EC] p-10 text-center text-[#0B2545]/60">No documents in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {filtered.map((doc) => (
                <div key={doc.id} className="group flex items-center justify-between gap-4 rounded-lg border border-[#E6DDC8] bg-[#FAF6EC] p-5 transition hover:border-[#C9A86A] hover:bg-white">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-white text-[#C9A86A] shadow-sm">
                      <FileText size={20} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-serif text-[15.5px] font-semibold text-[#0B2545]">{doc.title}</div>
                      <div className="text-[12px] text-[#0B2545]/60">
                        {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'No file uploaded'}
                      </div>
                    </div>
                  </div>
                  {doc.file_url ? (
                    <a href={resolveImage(doc.file_url)} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#0B2545] px-3.5 py-2 text-[12.5px] font-medium text-[#0B2545] transition hover:bg-[#0B2545] hover:text-white">
                      <Download size={14} /> Download
                    </a>
                  ) : (
                    <span className="text-[12px] text-[#0B2545]/45">No file</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default DocumentsPage;
